// Cross-filter store. Slicers and visual click-throughs write here; visuals
// read merged filters via selectByVisual().

import { create } from "zustand";

export type FilterOp =
  | "eq"
  | "neq"
  | "in"
  | "not_in"
  | "gt"
  | "gte"
  | "lt"
  | "lte"
  | "between"
  | "contains";

export type FilterValue = string | number | boolean | null | Array<string | number | boolean>;

export interface FilterEntry {
  op: FilterOp;
  value: FilterValue;
  /** Optional id of the visual that authored this filter — used for
   *  "don't filter the visual that emitted the click" semantics. */
  source?: string;
}

/** Key is `${table}.${column}`. */
export type FilterKey = string;

interface FilterState {
  filters: Record<FilterKey, FilterEntry>;
  addFilter: (key: FilterKey, entry: FilterEntry) => void;
  removeFilter: (key: FilterKey) => void;
  clearAll: () => void;
  /** Toggle behavior for slicer chips: if same value already set, drop it. */
  toggleFilter: (key: FilterKey, entry: FilterEntry) => void;
  /**
   * Returns filters relevant to a visual.
   * Cross-filter semantics: skip filters authored by `visualId` itself
   * (a visual should not filter itself), but include all others.
   */
  selectByVisual: (visualId: string) => Record<FilterKey, FilterEntry>;
  // P2-drill: per-visual drill level into a date/category hierarchy.
  // Key is the visualId; value is the grain index (0=Year, 1=Quarter,
  // 2=Month, 3=Day). Defaults to 0 (top of hierarchy) when missing.
  drillLevels: Record<string, number>;
  setDrillLevel: (visualId: string, level: number) => void;
  resetDrillLevels: () => void;
}

export const useFilterStore = create<FilterState>((set, get) => ({
  filters: {},
  addFilter: (key, entry) =>
    set((s) => ({ filters: { ...s.filters, [key]: entry } })),
  removeFilter: (key) =>
    set((s) => {
      const next = { ...s.filters };
      delete next[key];
      return { filters: next };
    }),
  clearAll: () => set({ filters: {} }),
  toggleFilter: (key, entry) =>
    set((s) => {
      const cur = s.filters[key];
      if (
        cur &&
        cur.op === entry.op &&
        JSON.stringify(cur.value) === JSON.stringify(entry.value)
      ) {
        const next = { ...s.filters };
        delete next[key];
        return { filters: next };
      }
      return { filters: { ...s.filters, [key]: entry } };
    }),
  selectByVisual: (visualId) => {
    const result: Record<FilterKey, FilterEntry> = {};
    for (const [k, v] of Object.entries(get().filters)) {
      if (v.source === visualId) continue;
      result[k] = v;
    }
    return result;
  },
  // P2-drill: drill-level state. Bounded to [0,3] at the setter so callers
  // can't push the level past Day or back above Year. Stored as a plain
  // map keyed by visualId.
  drillLevels: {},
  setDrillLevel: (visualId, level) =>
    set((s) => {
      const bounded = Math.max(0, Math.min(3, Math.trunc(level)));
      return { drillLevels: { ...s.drillLevels, [visualId]: bounded } };
    }),
  resetDrillLevels: () => set({ drillLevels: {} }),
}));

/** Helper: build a stable `${table}.${column}` key. */
export function filterKey(table: string, column: string): FilterKey {
  return `${table}.${column}`;
}
